import { useSelector } from "react-redux";
import { selectUIVersion } from "store/app";
import { Box, Text, useThemeUI, Button as ThemeUiButton } from "theme-ui";
import { useDispatch } from "react-redux";
import isNil from "lodash/fp/isNil";

import { Button } from "components";
import ToolTip from "components/ToolTip";
import { displayDetailsModal } from "store/app/appSlice";
import { CheckmarkIcon, InfoIcon } from "components/icons";
import * as styles from "./ButtonOptions.styles";
import { useDefaultValuesOnFirstRender } from "hooks";

export default function ButtonOptions({
  options = [],
  value: selectedValue,
  hint,
  disableUnselect = false,
  isSelected: isSelectedOverride,
  onChange,
  defaultValueToSetOnRender,
  goBack,
}) {
  const {
    theme: { isMobile },
  } = useThemeUI();

  const UIVersion = useSelector(selectUIVersion);

  const dispatch = useDispatch();

  useDefaultValuesOnFirstRender({
    options,
    defaultValueToSetOnRender,
    onChange,
    selectedValue,
  });

  const optionButtons = options.map((option) => {
    const isSelected = selectedValue === option.value;

    const isSelectedOrOverride = !isNil(isSelectedOverride)
      ? isSelectedOverride
      : isSelected;

    const displayToolTipAsMobileModal =
      isMobile &&
      UIVersion === 1 &&
      option.toolTip &&
      option.displayToolTipAsMobileModal !== false;

    return {
      key: option.value,
      toolTip: option?.toolTip,
      component: (
        <Button
          key={option.value}
          variant="secondary"
          size={isMobile ? "medium" : "large"}
          iconBefore={isSelectedOrOverride && <CheckmarkIcon />}
          isRound
          isSelected={isSelectedOrOverride}
          disabled={option.isDisabled}
          onClick={() => {
            if (disableUnselect && isSelected) {
              goBack();
            }
            onChange(option.value);
          }}
          sx={{ userSelect: "none" }}
        >
          {option.label}
        </Button>
      ),
      modal: displayToolTipAsMobileModal && (
        <ThemeUiButton
          variant="unstyled"
          sx={{
            height: "26px",
            width: "26px",
            display: "block",
            flexGrow: "0",
            flexShrink: "0",
          }}
          onClick={() => {
            dispatch(
              displayDetailsModal({
                title: option.toolTip.props.modalTitle,
                subTitle: option.value,
                content: option.toolTip.props.label,
              })
            );
          }}
        >
          <InfoIcon />
        </ThemeUiButton>
      ),
    };
  });

  return optionButtons
    .map(({ key, toolTip, component, modal }) => {
      if (modal) {
        return (
          <Box key={key} sx={styles.optionButtonMobileContainer}>
            {component}
            {modal}
          </Box>
        );
      }

      if (toolTip) {
        return (
          <ToolTip key={key} toolTip={toolTip}>
            {component}
          </ToolTip>
        );
      }

      return component;
    })
    .concat(
      hint
        ? [
            <Text as="div" key="hint" sx={styles.hint}>
              {hint}
            </Text>,
          ]
        : []
    );
}
